import Day from "containers/Day";
import Directory from "containers/Directory";
import HumanWin from "containers/HumanWin";
import Intro from "containers/Intro";
import Morning from "containers/Morning";
import Night from "containers/Night";
import Polls from "containers/Polls";
import VampireWin from "containers/VampireWin";
import WaitingRoom from "containers/WaitingRoom";
import { actions, gameStateType } from "core/constants";
import { isCmdD, isEscapeKey } from "core/utils/keyDown";
import { useToggleModalWithSound } from "hooks/navigation";
import { usePlaySoundEffectFromApi } from "hooks/sound";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Switch, useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import { toggleDevButton } from "reducer/dev";
import { getIsModalOpen } from "reducer/modalState";
import { SESSION_NAME, getOption } from "reducer/options";
import ExitModal from "./ExitModal";
import VIPExitButton from "components/VIPExitButton";
import GameHistoryTimeline from "components/GameHistoryTimeline";
import { maybeGetTestGameSession } from "utils/sessionHelper";

const {
  CREATE_SESSION,
  ENDED,
  PLAYER_ADDED,
  UPDATED_SESSION_GAME_STATE,
  UPDATE_SESSION_GAME_STATE,
  UPDATE_GAME_SETTINGS,
  UPDATED_GAME_SETTINGS,
  PING_SERVER,
  DESTROY_SESSION,
  START_GAME,
  FRESH_START,
} = actions;
const {
  WAITING,
  INTRO,
  READY,
  STARTED,
  HUMAN_WIN,
  VAMPIRE_WIN,
  NIGHT_INTRO,
  NIGHT,
  MORNING_INTRO,
  MORNING,
  DAY,
  POLLS,
} = gameStateType;

const FloatingExitButton = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 100;
`;

const PlayGame = ({ connected, socket }) => {
  const history = useHistory();
  const location = useLocation();
  const [gameSession, setGameSession] = useState(null);
  const _sessionName = useSelector(getOption(SESSION_NAME));
  const sessionName = maybeGetTestGameSession(_sessionName);
  const showModal = useSelector(getIsModalOpen);
  const toggleModal = useToggleModalWithSound();

  const dispatch = useDispatch();

  // Detect if we're in desktop mode and use appropriate base path
  const basePath = useMemo(() => {
    return location.pathname.startsWith("/desktop")
      ? "/desktop/PlayGame"
      : "/PlayGame";
  }, [location.pathname]);

  const onPressKey = useCallback(
    (e) => {
      if (isCmdD(e)) {
        dispatch(toggleDevButton());
      }
      if (isEscapeKey(e)) {
        return toggleModal();
      }
    },
    [dispatch, toggleModal]
  );

  const [players, setPlayers] = useState([]);
  const [polls, setPolls] = useState();
  const [gameSettings, setGameSettings] = useState(null);
  const { error, data } = gameSession || {};

  const {
    introRound,
    gameSessionId: _gameSessionId,
    condemned,
    message,
    gameState = WAITING,
    soundEffects,
  } = data || {};
  const gameSessionId = maybeGetTestGameSession(_gameSessionId);

  useEffect(() => {
    if (gameState === WAITING) {
      history.push(`${basePath}/WaitingRoom`);
    } else if ([INTRO, NIGHT_INTRO, MORNING_INTRO].includes(gameState)) {
      history.push(`${basePath}/${INTRO}`);
    } else if (gameState) {
      // console.log({ gameState });
      history.push(`${basePath}/${gameState}`);
    }
  }, [gameState, history, basePath]);

  usePlaySoundEffectFromApi(soundEffects, gameState);

  useEffect(() => {
    document.addEventListener("keydown", onPressKey);
    return () => {
      document.removeEventListener("keydown", onPressKey);
    };
  }, [onPressKey]);

  useEffect(() => {
    if (!gameSessionId) {
      socket.emit(
        CREATE_SESSION,
        {
          sessionName,
        },
        (_gameSession) => {
          console.log("🔵 CREATE_SESSION callback:", {
            _gameSession,
            settings: _gameSession?.data?.settings,
          });
          setGameSession(_gameSession);
          const gameSessionPlayers = _gameSession?.data?.players;
          if (gameSessionPlayers?.length > 0) {
            setPlayers(gameSessionPlayers);
          }
          setPolls(_gameSession?.data?.polls);
          const settings = _gameSession?.data?.settings;
          console.log("📝 Setting gameSettings to:", settings);
          if (!settings) {
            console.error("❌ ERROR: Settings is null/undefined!", {
              _gameSession,
            });
          }
          setGameSettings(settings);
        }
      );
    }
  }, [socket, gameSessionId, sessionName]);
  useEffect(() => {
    if (gameSessionId) {
      console.log("🔔 PlayGame: registering session listeners", {
        gameSessionId,
      });
      socket.on(`${gameSessionId}-${PLAYER_ADDED}`, function (player) {
        setPlayers((p) => p.concat(player));
      });
      socket.on(
        `${gameSessionId}-${UPDATED_SESSION_GAME_STATE}`,
        function (response) {
          const {
            gameState,
            message,
            condemned,
            soundEffects,
            players,
            polls,
            introRound,
          } = response;
          if (gameState) {
            setGameSession((g) => ({
              ...g,
              data: {
                ...g.data,
                gameState,
                message,
                condemned,
                soundEffects,
                introRound,
              },
            }));
          }
          if (players) {
            setPlayers(players);
          }
          setPolls(polls);
        }
      );
      socket.on(
        `${gameSessionId}-${UPDATED_GAME_SETTINGS}`,
        function ({ settings }) {
          console.log("🔶 PlayGame: UPDATED_GAME_SETTINGS received", {
            settings,
          });
          setGameSettings(settings);
        }
      );
      // TODO: Create reusable method for below (used above)
      socket.on(`${gameSessionId}-${ENDED}`, function (_gameSession) {
        socket.emit(
          CREATE_SESSION,
          {
            sessionName,
          },
          (_gameSession) => {
            setGameSession(_gameSession);
            setPlayers([]);
          }
        );
      });
    }

    // Cleanup function to remove listeners
    return () => {
      if (gameSessionId) {
        socket.off(`${gameSessionId}-${PLAYER_ADDED}`);
        socket.off(`${gameSessionId}-${UPDATED_SESSION_GAME_STATE}`);
        socket.off(`${gameSessionId}-${UPDATED_GAME_SETTINGS}`);
        socket.off(`${gameSessionId}-${ENDED}`);
      }
    };
  }, [socket, gameSessionId, sessionName]);

  useEffect(() => {
    // Ping server to keep session alive
    let interval;
    if (gameSessionId) {
      interval = setInterval(() => {
        socket.emit(PING_SERVER, {
          gameSessionId,
        });
      }, 15000);
    }
    return () => {
      clearInterval(interval);
    };
  }, [socket, gameSessionId]);

  const onExitGame = () => {
    socket.emit(DESTROY_SESSION, {
      sessionName,
    });
    history.push("/");
  };

  // Session should only be destroyed on explicit quit, not on refresh
  // Removed beforeunload handler - sessions persist across refreshes

  const showSpinner = !error && (!gameSessionId || !connected);

  const waiting = gameState === WAITING;

  const moveToNextGameState = () => {
    socket.emit(UPDATE_SESSION_GAME_STATE, {
      gameSessionId,
    });
  };

  const gotToState = (_gameState) => {
    socket.emit(UPDATE_SESSION_GAME_STATE, {
      gameSessionId,
      gameState: _gameState,
    });
  };

  if (condemned) {
    debugger;
  }
  const afterShowResults = () => {
    const nextState = condemned ? NIGHT : DAY;
    console.log({ nextState, condemned });
    socket.emit(UPDATE_SESSION_GAME_STATE, {
      gameSessionId,
      gameState: nextState,
    });
  };

  const restartRound = () => {
    socket.emit(START_GAME, { gameSessionId });
  };

  const restartRoundNoIntro = (options = {}) => {
    socket.emit(START_GAME, {
      gameSessionId,
      skipIntro: true,
      roleSeed: options.roleSeed,
    });
  };

  const handleSaveSettings = (newSettings) => {
    socket.emit(
      UPDATE_GAME_SETTINGS,
      {
        gameSessionId,
        settings: newSettings,
      },
      (response) => {
        if (response.status !== 200) {
          alert(response.error);
        }
      }
    );
  };

  const handleVIPExit = useCallback(() => {
    socket.emit(FRESH_START, {
      gameSessionId,
      playerId: localStorage.getItem("Player Id"),
    });
    history.push("/");
  }, [socket, gameSessionId, history]);

  return (
    <>
      <Switch>
        <Route path={`${basePath}/WaitingRoom`}>
          {(() => {
            const playerId = localStorage.getItem("Player Id");
            const isVIPCalc = players.some((p) => p.vip && playerId === p.id);

            // Calculate vipReady based on player count vs minPlayers from settings
            const minPlayers = gameSettings?.roleConfig?.minPlayers || 6;
            const vipReady = isVIPCalc && players.length >= minPlayers;

            console.log("🔍 PlayGame WaitingRoom render:", {
              players,
              playerId,
              isVIP: isVIPCalc,
              gameSettings,
              vipPlayer: players.find((p) => p.vip),
              minPlayers,
              vipReady,
            });
            return (
              <WaitingRoom
                error={error}
                players={players}
                gameSessionId={gameSessionId}
                showSpinner={showSpinner}
                waiting={waiting}
                vipReady={vipReady}
                settings={gameSettings}
                onSaveSettings={handleSaveSettings}
                isVIP={isVIPCalc}
                isSettingsLocked={gameState !== WAITING}
                startGame={restartRoundNoIntro}
                startGameWithIntro={restartRound}
                onVIPExit={handleVIPExit}
                gameState={gameState}
              />
            );
          })()}
        </Route>
        <Route path={`${basePath}/${INTRO}`}>
          <Intro
            pause={showModal}
            onComplete={moveToNextGameState}
            players={players}
            gameState={gameState}
            vipPlayer={players.find(({ vip }) => vip)?.userName}
            restartRound={restartRound}
            restartRoundNoIntro={restartRoundNoIntro}
          />
        </Route>
        <Route path={`${basePath}/${STARTED}`}>
          <Directory
            pause={showModal}
            onComplete={moveToNextGameState}
            players={players}
            restartRound={restartRound}
            restartRoundNoIntro={restartRoundNoIntro}
            timerDuration={gameSettings?.timers?.directory}
          />
        </Route>
        <Route path={`${basePath}/${NIGHT}`}>
          <Night
            pause={showModal}
            onComplete={moveToNextGameState}
            introRound={introRound}
            restartRound={restartRound}
            restartRoundNoIntro={restartRoundNoIntro}
            timerDuration={gameSettings?.timers?.night}
          />
        </Route>
        <Route path={`${basePath}/${MORNING}`}>
          <Morning
            pause={showModal}
            onComplete={moveToNextGameState}
            goToNight={() => gotToState(NIGHT)}
            message={message}
            players={players}
            introRound={introRound}
            restartRound={restartRound}
            restartRoundNoIntro={restartRoundNoIntro}
            timerDuration={gameSettings?.timers?.morning}
          />
        </Route>
        <Route path={`${basePath}/${DAY}`}>
          <Day
            pause={showModal}
            onComplete={moveToNextGameState}
            goToNight={() => gotToState(NIGHT)}
            message={message}
            introRound={introRound}
            restartRound={restartRound}
            restartRoundNoIntro={restartRoundNoIntro}
            timerDuration={gameSettings?.timers?.day}
          />
        </Route>
        <Route path={`${basePath}/${POLLS}`}>
          <Polls
            pause={showModal}
            onComplete={afterShowResults}
            goToNight={() => gotToState(NIGHT)}
            polls={polls}
            message={message}
            condemnedPlayer={players.find(({ id }) => id === condemned)}
            restartRound={restartRound}
            restartRoundNoIntro={restartRoundNoIntro}
            timerDuration={gameSettings?.timers?.polls}
          />
        </Route>
        <Route path={`${basePath}/${HUMAN_WIN}`}>
          <HumanWin players={players} />
        </Route>
        <Route path={`${basePath}/${VAMPIRE_WIN}`}>
          <VampireWin players={players} />
        </Route>
      </Switch>
      <ExitModal players={players} onExitGame={onExitGame} />

      {/* Floating VIP exit button - visible on all screens */}
      {(() => {
        const playerId = localStorage.getItem("Player Id");
        const isVIP = players.some((p) => p.vip && playerId === p.id);

        return (
          isVIP &&
          gameSessionId && (
            <FloatingExitButton>
              <VIPExitButton
                onConfirmExit={handleVIPExit}
                gameState={gameState}
              />
            </FloatingExitButton>
          )
        );
      })()}

      {/* Game History Timeline - visible for debugging or as a tab/section */}
      {gameSessionId && (
        <GameHistoryTimeline socket={socket} gameSessionId={gameSessionId} />
      )}
    </>
  );
};
export default PlayGame;
